import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { ProductsTypeEnum } from '@app/forms/subscription/definition/interface';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { Observable, map } from 'rxjs';
import { Criteria } from '../criteria/criteria';
import { Dao } from '../dao';
import { DataLoader } from '../data-loader';
import { ProductElement } from './product.type';

export class ProvidersCriteria extends Criteria {
  productType?:
    | 'ASSURANCE_VIE'
    | 'PER'
    | 'PEA'
    | 'PEA_PME'
    | 'GIRARDIN'
    | 'SOFICA'
    | 'FCPI'
    | 'FIP'
    | 'FIP_CORSE'
    | 'FIP_DOMTOM'
    | 'PME'
    | 'CROWDFUNDING'
    | 'CONTRAT_CAPI'
    | 'COMPTE_TITRE'
    | 'COMPTE_CRYPTO'
    | 'COMPTE_COURANT'
    | 'PEE'
    | 'PEI'
    | 'PERP'
    | 'PERCO'
    | 'PREFON'
    | 'ACTIF_EN_DIRECT'
    | 'CODEVI'
    | 'LDD'
    | 'PEL'
    | 'CEL'
    | 'LIVRET_A'
    | 'LIVRET_B'
    | 'CSL'
    | 'LIVRET'
    | 'SOCIAL_SHARE'
    | 'BORROWER_INSURANCE'
    | 'CONTINGENCY'
    | 'INSURANCE'
    | 'LOAN_REDEMPTION'
    | 'WINE'
    | 'FOREST'
    | 'SOLAR'
    | 'PIECE_OF_ART'
    | 'COLLECTION'
    | 'BOND'
    | 'GFV'
    | 'OPCI'
    | 'GFI'
    | 'GFF'
    | 'CRYPTO'
    | 'NFT'
    | 'SCPI'
    | 'UNFURNISHED'
    | 'LMNP'
    | 'LOAN'
    | 'OTHER'
    | 'DEFAULT';
}
export class ProductCriteria extends Criteria {
  autocomplete?: string;
  enabled?: boolean;
  status?: string;
  categories?: string[];
  type?: string[];
  subscriptionFluxPartner?: string[];
  ids?: string[];
  organizationIds?: number[];
  onlineSubscription?: boolean;
  mappedSubscriptionForm?: boolean;
  size?: number;
}

export class AllocationModelCriteria extends Criteria {
  productId?: number;
  productType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductDao extends Dao {
  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }
  private productTypes: DataLoader<any> = new DataLoader<any>(() =>
    this.httpApi.get(`/productTypes`),
  );
  /* Assurance vie */
  getProducts(criteria?: ProductCriteria): Observable<any> {
    return this.httpApi
      .post(`/products/global/search`, criteria || { type: ['ASSURANCE_VIE'] })
      .pipe(map(res => res['content']));
  }
  getProductsGeneral(criteria?: ProductCriteria): Observable<any> {
    const isCrowfundingSubscription =
      criteria?.type?.length === 1 &&
      criteria?.type[0] === ProductsTypeEnum.CROWDFUNDING;

    return this.httpApi
      .post(`/products/search`, criteria || { type: ['ASSURANCE_VIE'] })
      .pipe(
        map(res => {
          const products = res['content'];
          return products.map(({ ...product }) => ({
            ...product,
            name: isCrowfundingSubscription
              ? `${product?.manager?.name} - ${product.name}`
              : product.name,
          }));
        }),
      );
  }

  getProduct(id: string): Observable<ProductElement> {
    return this.httpApi.get(`/products/${id}`);
  }

  getProductType(): Observable<any> {
    return this.productTypes.get();
  }

  getPartners(criteria?: ProductCriteria): Observable<any> {
    return this.httpApi
      .post(`/partner/global/search`, criteria)
      .pipe(map(res => res['content']));
  }

  getProviders(criteria?: ProvidersCriteria): Observable<any> {
    return this.httpApi
      .post(`/providers/search`, criteria)
      .pipe(map(res => res['content']));
  }

  proposeProvider(name: string, productType: string): Observable<any> {
    return this.httpApi.post(`/providers/propose`, { name, productType });
  }

  proposeProduct(name: string, productType: string, partnerId: number): Observable<any> {
    return this.httpApi.post(`/products/propose`, { name, productType, partnerId, availability: 'ALL' });
  }

  getGlobalByProductTypeAndOrganizationId(
    productType: string,
    organizationId: number,
  ): Observable<any> {
    return this.httpApi.get(
      `/products/${productType}/organizations/${organizationId}/global`,
    );
  }

  getDigitalInsureProduct(productCode: string): Observable<any> {
    return this.httpApi.get(`/digital-insure/products/${productCode}`);
  }

  getDigitalInsureProducts(): Observable<any> {
    return this.httpApi.get(`/digital-insure/products`);
  }

  getDigitalInsureConfiguration(): Observable<any> {
    return this.httpApi.get(`/digital-insure/configuration`);
  }

  getDigitalInsureProfessionnalCategoryRef(): Observable<any> {
    return this.httpApi.get(`/digital-insure/references/professional-category`);
  }

  getDigitalInsureProfessionnalSubCategoryRef(): Observable<any> {
    return this.httpApi.get(
      `/digital-insure/references/professional-category-sub`,
    );
  }

  getDigitalInsureProfessionnalSocialRegimeRef(): Observable<any> {
    return this.httpApi.get(`/digital-insure/references/social-regime`);
  }

  getPrevoyanceProtectLovedRef(): Observable<any> {
    return this.httpApi.get(`/ref/product/prevoyance-protect-loved`);
  }

  getPrevoyanceMaintainIncome(): Observable<any> {
    return this.httpApi.get(`/ref/product/prevoyance-maintain-income`);
  }

  getBaremeInvaliditeRef(): Observable<any> {
    return this.httpApi.get(`/ref/bareme-invalidite`);
  }

  getRegimeObligatoireRef(): Observable<any> {
    return this.httpApi.get(`/ref/regime-obligatoire`);
  }

  getPrevoyanceDevisList(accountId: number): Observable<any[]> {
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/prevoyance-personnelle/devis`,
    );
  }

  getProductById(productId: number): Observable<any> {
    return this.httpApi.get(`/products/${productId}`);
  }

  updatePrevoyanceDevis(accountId: number, devisId: number, body: any): Observable<any> {
    return this.httpApi.put(
      `/accounts/${accountId}/prevoyance/prevoyance-personnelle/devis/${devisId}`,
      body,
    );
  }

  savePrevoyanceHypotheses(accountId: number, body: any): Observable<any> {
    return this.httpApi.post(
      `/accounts/${accountId}/prevoyance/prevoyance-personnelle/hypotheses`,
      body,
    );
  }

  getPrevoyanceDevisById(accountId: number, devisId: number): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/prevoyance-personnelle/devis/${devisId}`,
    );
  }

  getPrevoyanceHypotheses(accountId: number, ownerId: number): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/prevoyance-personnelle/hypotheses/${ownerId}`,
    );
  }

  getAeDevisById(accountId: number, devisId: number): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/assurance-emprunteur/devis/${devisId}`,
    );
  }

  getMutuelleDevisList(accountId: number): Observable<any[]> {
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/mutuelle/devis`,
    );
  }

  getMutuelleDevisById(accountId: number, devisId: number): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/mutuelle/devis/${devisId}`,
    );
  }

  updateMutuelleDevis(accountId: number, devisId: number, body: any): Observable<any> {
    return this.httpApi.put(
      `/accounts/${accountId}/prevoyance/mutuelle/devis/${devisId}`,
      body,
    );
  }

  saveMutuelleHypotheses(accountId: number, body: any): Observable<any> {
    return this.httpApi.post(
      `/accounts/${accountId}/prevoyance/mutuelle/hypotheses`,
      body,
    );
  }

  getMutuelleHypotheses(accountId: number, ownerId: number): Observable<any> {
    return this.httpApi.get(
      `/accounts/${accountId}/prevoyance/mutuelle/hypotheses/${ownerId}`,
    );
  }

  getOptiqueVisionRef(): Observable<any> {
    return this.httpApi.get(`/ref/optique-vision`);
  }

  getDentaireSoinsRef(): Observable<any> {
    return this.httpApi.get(`/ref/dentaire-soins`);
  }

  downloadDigitalInsureTarificationDocument(
    productCode: string,
    insurerReference: string,
    docTypeCode: string,
  ): Observable<Blob> {
    return this.httpApi.getWithOptions(
      `/digital-insure/download-tarification-document?productCode=${productCode}&insurerReference=${insurerReference}&docTypeCode=${docTypeCode}`,
      { responseType: 'blob' },
    );
  }

  /* state */
  onUserChange(event: any) {}

  reset() {}
}
